import React, { useState, useEffect } from 'react';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Mail, Phone, MapPin, Linkedin, Send, MessageCircle, Calendar, ExternalLink } from 'lucide-react';
import { usePopupContext } from '../contexts/PopupContext';
import { useSectionAnimationContext } from '../contexts/SectionAnimationContext';
import { contact_content } from '../data/content';
import LiquidBlobs from './LiquidBlobs';

const ContactSection = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const { showSuccess, showInfo, showError } = usePopupContext();
  const { getSectionClasses } = useSectionAnimationContext();

  // Use static data instead of API call
  const contactContent = contact_content[0] || null;

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    // Mock form submission
    console.log('Form submitted:', formData);
    showSuccess(
      `Thank you ${formData.name}! Your message has been sent successfully. I'll get back to you within 24 hours.`,
      'Message Sent Successfully!'
    );
    setFormData({ name: '', email: '', subject: '', message: '' });
  };

  const handleWhatsApp = () => {
    // Open WhatsApp with pre-filled message
    const message = encodeURIComponent("Hi Gideon, I'm interested in discussing a potential collaboration or opportunity.");
    const whatsappUrl = `https://wa.me/918087613486?text=${message}`;
    window.open(whatsappUrl, '_blank');
    showInfo(
      'WhatsApp is now opening with a pre-filled professional message. Feel free to customize it before sending!',
      'WhatsApp Chat Started'
    );
  };

  const handleScheduleCall = () => {
    // Mock scheduling - in real implementation, this would integrate with a calendar service
    showInfo(
      'Call scheduling will be available soon! For now, please use the contact form above or WhatsApp to arrange a call. I typically respond within 4-6 hours during business days.',
      'Schedule Call Feature'
    );
  };

  return (
    <section id="contact" className="py-24 bg-gradient-to-b from-gray-50 to-gray-100 relative overflow-hidden">
      <LiquidBlobs section="contact" />
      <div className="container mx-auto px-6 relative z-10">
        {/* Section content with genie animation */}
        <div className={getSectionClasses('contact')}>
        {/* Section Header */}
        <div className="mb-16 text-center">
          <h2 className="heading-1 text-5xl md:text-7xl font-black text-text-primary mb-4 font-grotesk uppercase">
            Let's <span className="text-rose-600">Connect</span>
          </h2>
          <div className="w-24 h-1 bg-rose-600 mx-auto mb-6"></div>
          <p className="body-medium text-text-secondary max-w-3xl mx-auto">
            {contactContent?.section_subtitle || "Ready to collaborate on your next AI implementation, automation project, or team building initiative? Let's discuss how we can drive innovation together."}
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12">
          {/* Contact Information */}
          <div className="space-y-8">
            <div>
              <h3 className="heading-3 text-text-primary mb-6">Get in Touch</h3>
              <p className="body-medium text-text-secondary mb-8">
                I'm always open to discussing new opportunities, sharing insights, or collaborating 
                on innovative projects in asset management and AI implementation.
              </p>
            </div>

            {/* Professional Networking */}
            <div className="space-y-4">
              {/* LinkedIn Card */}
              <Card className="liquid-glass glass-card p-6 hover:shadow-lg transition-all duration-300">
                <div className="flex items-center space-x-4">
                  <div className="w-12 h-12 bg-cyan-100 rounded-full flex items-center justify-center backdrop-blur-sm">
                    <Linkedin size={24} className="text-cyan-600" />
                  </div>
                  <div className="flex-1">
                    <h4 className="heading-6 text-text-primary">Professional Network</h4>
                    <a
                      href={contactContent?.linkedin_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-text-secondary hover:text-cyan-600 transition-colors duration-300 flex items-center"
                    >
                      Connect on LinkedIn
                      <ExternalLink size={16} className="ml-1" />
                    </a>
                  </div>
                </div>
              </Card>
            </div>

            {/* Availability Status */}
            <Card className="liquid-glass glass-card p-6 border-l-4 border-l-green-400">
              <div className="flex items-center space-x-3 mb-3">
                <div className="w-3 h-3 bg-green-500 rounded-full animate-pulse"></div>
                <Badge className="bg-green-100 text-green-700 border-green-200 font-medium">
                  {contactContent?.availability_status || "Available for Opportunities"}
                </Badge>
              </div>
              <p className="text-text-secondary text-sm">
                {contactContent?.availability_text}
              </p>
            </Card>
          </div>

          {/* Contact Form */}
          <Card className="liquid-glass glass-card p-8">
            <div className="mb-6">
              <h3 className="heading-3 text-text-primary mb-2">Send a Message</h3>
              <p className="text-text-secondary">
                Have a specific project in mind? Let's discuss the details.
              </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-text-primary text-sm font-medium mb-2">
                    Name
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 glass-card border border-gray-200 rounded-lg text-text-primary placeholder-text-muted focus:outline-none focus:ring-2 focus:ring-brand-primary/50 focus:border-brand-primary/50 transition-all duration-300 backdrop-blur-sm"
                    placeholder="Your full name"
                  />
                </div>
                <div>
                  <label className="block text-text-primary text-sm font-medium mb-2">
                    Email
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 glass-card border border-gray-200 rounded-lg text-text-primary placeholder-text-muted focus:outline-none focus:ring-2 focus:ring-brand-primary/50 focus:border-brand-primary/50 transition-all duration-300 backdrop-blur-sm"
                    placeholder="your.email@example.com"
                  />
                </div>
              </div>

              <div>
                <label className="block text-text-primary text-sm font-medium mb-2">
                  Subject
                </label>
                <input
                  type="text"
                  name="subject"
                  value={formData.subject}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-3 glass-card border border-gray-200 rounded-lg text-text-primary placeholder-text-muted focus:outline-none focus:ring-2 focus:ring-brand-primary/50 focus:border-brand-primary/50 transition-all duration-300 backdrop-blur-sm"
                  placeholder="What would you like to discuss?"
                />
              </div>

              <div>
                <label className="block text-text-primary text-sm font-medium mb-2">
                  Message
                </label>
                <textarea
                  name="message"
                  rows={6}
                  value={formData.message}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-3 glass-card border border-gray-200 rounded-lg text-text-primary placeholder-text-muted focus:outline-none focus:ring-2 focus:ring-brand-primary/50 focus:border-brand-primary/50 transition-all duration-300 resize-none backdrop-blur-sm"
                  placeholder="Tell me about your project, goals, or how we can collaborate..."
                />
              </div>

              <Button type="submit" className="btn-base btn-rose btn-lg w-full group">
                <Send size={20} className="mr-2 group-hover:translate-x-1 transition-transform" />
                Send Message
              </Button>
            </form>

            {/* Quick Actions */}
            <div className="mt-8 pt-6 border-t border-gray-200">
              <p className="text-text-secondary text-sm mb-4">Prefer a quick chat?</p>
              <div className="flex gap-3">
                <Button 
                  onClick={handleWhatsApp}
                  className="btn-base btn-emerald btn-sm flex-1"
                >
                  <MessageCircle size={16} className="mr-2" />
                  WhatsApp
                </Button>
                <Button 
                  onClick={handleScheduleCall}
                  className="btn-base btn-primary btn-sm flex-1"
                >
                  <Calendar size={16} className="mr-2" />
                  Schedule Call
                </Button>
              </div>
            </div>
          </Card>
        </div>
        {/* End genie animation wrapper */}
        </div>
      </div>
    </section>
  );
};

export default ContactSection;